import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap, shareReplay, finalize } from 'rxjs/operators';

import { IdentityReportsService } from '@app/core/services/identity-reports.service';

@Injectable({ providedIn: 'root' })
export class IdentityDocumentBlobCacheService {
  private readonly cache = new Map<string, Blob>();
  private readonly inflight = new Map<string, Observable<Blob>>();

  constructor(private readonly api: IdentityReportsService) {}

  get$(civilId: string, docId: number): Observable<Blob> {
    const key = this.key(civilId, docId);

    const cached = this.cache.get(key);
    if (cached) return of(cached);

    const existing = this.inflight.get(key);
    if (existing) return existing;

    const req$ = this.api.renderIdentityDocumentContent(civilId, docId).pipe(
      map((raw: Blob) => new Blob([raw as any])),
      tap((blob) => this.cache.set(key, blob)),
      finalize(() => this.inflight.delete(key)),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.inflight.set(key, req$);
    return req$;
  }

  has(civilId: string, docId: number): boolean {
    return this.cache.has(this.key(civilId, docId));
  }

  peek(civilId: string, docId: number): Blob | null {
    return this.cache.get(this.key(civilId, docId)) ?? null;
  }

  evictDoc(civilId: string, docId: number): void {
    this.cache.delete(this.key(civilId, docId));
    this.inflight.delete(this.key(civilId, docId));
  }

  evictClient(civilId: string): void {
    const prefix = `${civilId}:`;
    for (const k of Array.from(this.cache.keys())) {
      if (k.startsWith(prefix)) this.cache.delete(k);
    }
    for (const k of Array.from(this.inflight.keys())) {
      if (k.startsWith(prefix)) this.inflight.delete(k);
    }
  }

  evictAll(): void {
    this.cache.clear();
    this.inflight.clear();
  }

  private key(civilId: string, docId: number): string {
    return `${civilId}:${docId}`;
  }
}
